class Transformation {
    constructor() {

    }

    mapTextureAnimationId(volumeList, modelId, animationId) {
        if (!modelId) {
            return;
        }

        const materials = modelManager.getMeshMaterials(modelId);
        
        if (!materials || materials.length === 0) {
            return;
        }
        
        materials.forEach(material => {
            for (let i = 0; i < volumeList.length; i++) {
                const volume = modelManager.getVolume(volumeList[i]);
                if (!volume) {
                    continue;
                }
                const id = volume.meshID - material.uniforms.startingMeshID.value;
                material.uniforms.animationMeshIdMapTexture.value.image.data[Math.floor(id)] = animationId;
            }

            material.uniforms.animationMeshIdMapTexture.value.needsUpdate = true;
        });
    }

    playAnimation(animationInfo) {
        if (![ModelTypeEnum.CAD, ModelTypeEnum.AR_MARKER, ModelTypeEnum.HELPER_OBJECT].includes(modelManager.getModelType(animationInfo.modelId))) {
            return;
        }

        const materials = modelManager.getMeshMaterials(animationInfo.modelId);
        const animation = animationInfo.animation;
        const totalKeyframe = animation.times.length;
        const pivot = animationInfo.pivot;
        animationInfo.isAllVolumeAnimating = this._isAnimatingAllVolumes(animationInfo);

        const self = this;
        let currentKeyframe = animationInfo.state.isPaused ? animationInfo.state.currentKeyframe : 1;

        function animateKeyframe(currentKeyframe, isStartingFrame) {
            if (currentKeyframe >= totalKeyframe) {
                if (!animationInfo.loop) {
                    self.finishAnimation(animationInfo);
                    return;
                } else {
                    currentKeyframe = 1;
                }
               
            }

            const startFrame = {
                pos: new THREE.Vector3(...animation.values[currentKeyframe - 1].position),
                rotationAxis: new THREE.Vector3(...animation.values[currentKeyframe - 1].normal),
                angle: animation.values[currentKeyframe - 1].angle
            };
            const endFrame = {
                pos: new THREE.Vector3(...animation.values[currentKeyframe].position),
                rotationAxis: new THREE.Vector3(...animation.values[currentKeyframe].normal),
                angle: animation.values[currentKeyframe].angle
            };
            let keyframeDuration = 1000 * (animation.times[currentKeyframe] - animation.times[currentKeyframe - 1]);
            let timeObj = {t: 0.0};
            if (animationInfo.state.isPaused) {
                timeObj = animationInfo.state.timeObj;
                keyframeDuration = keyframeDuration * (1 - timeObj.t);
                animationInfo.state.isPaused = false;
            } else {
                animationInfo.state.timeObj = timeObj;
                animationInfo.state.currentKeyframe = currentKeyframe;
            }

            animationInfo.tween = new TWEEN.Tween(timeObj)
                .to({t: 1.0}, keyframeDuration)
                .onStart(() => {
                    if (isStartingFrame) {
                        materials.forEach(material => {
                            material.uniforms.uRunningAnimationCount.value += 1;
                            material.uniforms.needsUpdate = true;
                        });

                        self.mapTextureAnimationId(animationInfo.volumeList, animationInfo.modelId, animationInfo.animationId);
                    }
                })
                .onUpdate(() => {
                    let transformMatrix = self.getMatrixBetweenKeyframes(startFrame, endFrame, pivot, timeObj);

                    if (animationInfo.state.pausedAtMatrix) {
                        const inversePrevMatrix = animationInfo.state.pausedAtMatrix.clone().invert();
                        if (animationInfo.meshType === AnimationPlayer.meshType.MODEL || animationInfo.isAllVolumeAnimating) {
                            transformMatrix = transformMatrix.premultiply(inversePrevMatrix);
                        } else {
                            transformMatrix = transformMatrix.multiply(inversePrevMatrix);
                        }
                    }

                    materials.forEach(material => {
                        const texture = material.uniforms.animationTransformationTexture.value;
                        const textureData = texture.image.data;
                        const offset = (animationInfo.animationId - 1) * 16;

                        for (let i = 0; i < 16; i++) {
                            textureData[offset + i] = transformMatrix.elements[i];
                        }

                        texture.needsUpdate = true;
                    });

                })
                .onComplete(() => {
                    animateKeyframe(currentKeyframe + 1);
                })
                .onStop(() => {
                    materials.forEach(material => {
                        material.uniforms.uRunningAnimationCount.value -= 1;
                        material.uniforms.needsUpdate = true;
                    });

                    if (animationInfo.state.isPaused) {
                        let currentMatrix = self.getMatrixBetweenKeyframes(startFrame, endFrame, pivot, timeObj);
                        self.pauseAnimation(animationInfo, currentMatrix);
                    } else {
                        self.finishAnimation(animationInfo);
                    }
                })
                .start();

        }

        animateKeyframe(currentKeyframe, true);
    }

    pauseAnimation(animationInfo, currentMatrix) {
        if (![ModelTypeEnum.CAD, ModelTypeEnum.AR_MARKER, ModelTypeEnum.HELPER_OBJECT].includes(modelManager.getModelType(animationInfo.modelId))) {
            return;
        }

        animationInfo.tween = null;

        const matrix = currentMatrix.clone();
        if (animationInfo.state.pausedAtMatrix) {
            const inversePrevMatrix = animationInfo.state.pausedAtMatrix.clone().invert();
            if (animationInfo.meshType === AnimationPlayer.meshType.MODEL || animationInfo.isAllVolumeAnimating) {
                currentMatrix = currentMatrix.premultiply(inversePrevMatrix);
            } else {
                currentMatrix = currentMatrix.multiply(inversePrevMatrix);
            }
        }

        animationInfo.state.pausedAtMatrix = matrix;

        this.applyMeshTransformation(currentMatrix, animationInfo);
        this.mapTextureAnimationId(animationInfo.volumeList, animationInfo.modelId, 0);
    }

    finishAnimation(animationInfo) {
        if (![ModelTypeEnum.CAD, ModelTypeEnum.AR_MARKER, ModelTypeEnum.HELPER_OBJECT].includes(modelManager.getModelType(animationInfo.modelId))) {
            return;
        }

        animationInfo.tween = null;

        const totalKeyframe = animationInfo.animation.times.length;
        const lastKeyFrame = animationInfo.animation.values[totalKeyframe - 1];
        let finalMatrix = this.getMatrixFromKeyframe(lastKeyFrame, animationInfo.pivot);

        if (animationInfo.state.pausedAtMatrix) {
            const inversePrevMatrix = animationInfo.state.pausedAtMatrix.clone().invert();
            if (animationInfo.meshType === AnimationPlayer.meshType.MODEL || animationInfo.isAllVolumeAnimating){
                finalMatrix = finalMatrix.premultiply(inversePrevMatrix);
            } else {
                finalMatrix = finalMatrix.multiply(inversePrevMatrix);
            }
        }

        this.applyMeshTransformation(finalMatrix, animationInfo);
        AnimationPlayer.removeFromPlayList(animationInfo);

        this.mapTextureAnimationId(animationInfo.volumeList, animationInfo.modelId, 0);
    }

    getMatrixBetweenKeyframes(startFrame, endFrame, pivot, timeObj) {
        const pos = new THREE.Vector3().lerpVectors(startFrame.pos, endFrame.pos, timeObj.t);

        let rotationAngle = startFrame.angle + (endFrame.angle - startFrame.angle) * timeObj.t;
        rotationAngle = THREE.MathUtils.degToRad(rotationAngle);

        const quat = new THREE.Quaternion().setFromAxisAngle(endFrame.rotationAxis.normalize(), rotationAngle);

        return this.getTransformMatrix(pos, quat, pivot);
    }

    getMatrixFromKeyframe(keyframe, pivot) {
        const pos = new THREE.Vector3(...keyframe.position);
        const rotationAxis = new THREE.Vector3(...keyframe.normal);
        const rotationAngle = THREE.MathUtils.degToRad(keyframe.angle);

        const quat = new THREE.Quaternion().setFromAxisAngle(rotationAxis.normalize(), rotationAngle);

        return this.getTransformMatrix(pos, quat, pivot);
    }

    getTransformMatrix(pos, quat, pivot) {
        const rotationMatrix = new THREE.Matrix4().makeRotationFromQuaternion(quat);

        const T_negPivot = new THREE.Matrix4().makeTranslation(-pivot.x, -pivot.y, -pivot.z);
        const T_pivot = new THREE.Matrix4().makeTranslation(pivot.x, pivot.y, pivot.z);
        const T_pos = new THREE.Matrix4().makeTranslation(pos.x, pos.y, pos.z);

        const transformMatrix = new THREE.Matrix4();
        transformMatrix.multiplyMatrices(T_pivot, rotationMatrix);
        transformMatrix.multiply(T_negPivot);
        transformMatrix.premultiply(T_pos);

        return transformMatrix;
    }

    applyMeshTransformation(finalMatrix, animationInfo) {
        const modelId = animationInfo.modelId;

        if (animationInfo.meshType === AnimationPlayer.meshType.MODEL || animationInfo.isAllVolumeAnimating) {
            const modelObj = getModelObject(modelId);
            if (!modelObj) {
                console.warn("Model not found.");
                return;
            }

            finalMatrix.premultiply(modelObj.matrix.clone());
            this._updateModelTransformation(modelObj, finalMatrix);
        } else {
            this._updateVolumeTransformation(animationInfo.volumeList, finalMatrix);
        }
    }

    _updateModelTransformation(modelObj, finalMatrix) {
        const position = new THREE.Vector3();
        const quaternion = new THREE.Quaternion();
        const scale = new THREE.Vector3();

        finalMatrix.decompose(position, quaternion, scale);

        modelObj.position.copy(position);
        modelObj.quaternion.copy(quaternion);
        modelObj.scale.copy(scale);
    }

    _updateVolumeTransformation(volumeList, finalMatrix) {
        changePerformMoveOnGroup(true);

        let volumeMatrix = new THREE.Matrix4();

        volumeList.forEach(branchId => {
            volumeMatrix.copy(finalMatrix);
            const volume = modelManager.getVolume(branchId);
            if (moved_volume_matrices.hasOwnProperty(volume.branchID)) {
                volumeMatrix.multiply(moved_volume_matrices[volume.branchID]);
            }

            moveVolume(volume, volumeMatrix.clone());
        });

        changePerformMoveOnGroup(false);
    }

    _isAnimatingAllVolumes({ modelId, volumeList: animatingBranchIds, state }) {
        const allBranchIdsInModel = modelManager.getBranchIds(modelId);

        if (animatingBranchIds.length !== allBranchIdsInModel.length) {
            return false;
        }

        const animatingBranchIdSet = new Set(animatingBranchIds);
        const allBranchIdSet = new Set(allBranchIdsInModel);

        if (animatingBranchIdSet.size !== allBranchIdSet.size) {
            return false;
        }

        for (let branchIdInModel of allBranchIdSet) {
            if (!animatingBranchIdSet.has(branchIdInModel)) {
                return false;
            }
        }

        if (!state.pausedAtMatrix) {
            alignModelWithTheSingleVolume(modelId);
        }

        return true;
    }
}

export default new Transformation();
